import React, { useEffect, useCallback, useReducer, useMemo } from 'react'
import { FallbackImage, ImageWrapper, Placeholder, StyledImage } from './styles'
import { isNavigatorOnline } from '../../Utils/Networking/networkHelpers'
import { useImageIntersectionObserver } from '../../Hooks/Observers/useImageIntersectionObserver'
import { useOnlineStatus } from '../../Hooks/useOnlineStatus'
import { imageReducer, initialState } from './ImgReducer'

type ImageProps = {
  src: string
  alt: string
  className?: string
}

export const Img: React.FC<ImageProps> = ({ src, alt, className }) => {
  const [state, dispatch] = useReducer(imageReducer, initialState)
  const { status, resolvedImageUrl, isOnline } = state

  useEffect(() => {
    dispatch({ type: 'RESET', payload: { src, isOnline: isNavigatorOnline() } })
  }, [src])

  useOnlineStatus(useCallback((online: boolean) => {
    dispatch({ type: 'SET_ONLINE', payload: online })
  }, []))

  const loadImage = useCallback(() => {
    if (!src) return

    dispatch({ type: 'LOAD_INITIATED' })

    if (src.startsWith('data:')) {
      dispatch({ type: 'LOAD_SUCCESS', payload: src })
      return
    }

    if (!isNavigatorOnline()) {
      dispatch({ type: 'SET_ONLINE', payload: false })
      return
    }

    const img = new Image()
    img.onload = () => dispatch({ type: 'LOAD_SUCCESS', payload: src })
    img.onerror = () => dispatch({ type: 'LOAD_FAILURE' })
    img.src = src
  }, [src])

  useEffect(() => {
    if (status === 'PENDING' && isOnline) {
      loadImage()
    }
  }, [status, isOnline, loadImage])

  const handleIntersection = useCallback((isIntersecting: boolean) => {
    if (isIntersecting) {
      dispatch({ type: 'INTERSECTING' })
    }
  }, [])

  const observerOptions = useMemo(() => ({ threshold: 0.1, rootMargin: '100px' }), [])
  const wrapperRef = useImageIntersectionObserver(handleIntersection, observerOptions)

  const showPlaceholder =
    status === 'IDLE' ||
    status === 'PENDING' ||
    status === 'LOADING' ||
    status === 'OFFLINE'
  const showImage = status === 'LOADED' && resolvedImageUrl
  const showErrorFallback = status === 'ERROR'

  return (
    <ImageWrapper
      ref={wrapperRef as React.RefObject<HTMLDivElement>}
      className={className}
    >
      {showPlaceholder && <Placeholder />}
      {showImage && <StyledImage src={resolvedImageUrl} alt={alt} />}
      {showErrorFallback && (
        <FallbackImage src={src} alt={alt} className="image-fallback" loading="lazy" />
      )}
    </ImageWrapper>
  )
}
